import { useState, useEffect, useCallback, useMemo } from 'react';
import { getContactEmailDisplay } from '@car-pool/shared/contactIdentity';
import { apiFetch } from '../config/api';
import ResetCodeButton from '../components/ResetCodeButton';
import SendOnboardingEmailButton from '../components/SendOnboardingEmailButton';
import SendOnboardingWhatsAppButton from '../components/SendOnboardingWhatsAppButton';

function parseSchoolIds(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function ParentDetail({ token, navigate, parentId, schoolId }) {
  const [parent, setParent] = useState(null);
  const [schoolStudents, setSchoolStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const [parentRes, studentsRes] = await Promise.all([
        apiFetch(`/contacts/${parentId}`, {}, token),
        apiFetch(`/students?school_id=${schoolId}`, {}, token),
      ]);
      const parentSchoolIds = parseSchoolIds(parentRes.data?.school_ids).map(Number);
      if (!parentSchoolIds.includes(Number(schoolId))) {
        throw new Error('Parent not found for this school');
      }
      setParent(parentRes.data);
      setSchoolStudents(studentsRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [parentId, schoolId, token]);

  useEffect(() => { load(); }, [load]);

  const childrenIds = parent?.children_ids || [];

  const linkedStudents = schoolStudents.filter((s) => childrenIds.includes(s.id));
  const allAvailable = schoolStudents.filter((s) => !childrenIds.includes(s.id) && s.status === 'active');

  // Smart matching: same last name or same address
  const suggestedStudents = useMemo(() => {
    if (!parent) return [];
    const parentLastName = parent.l_name?.toLowerCase() || '';
    const parentAddr = parent.street_address?.toLowerCase()?.trim() || '';
    return allAvailable.filter((s) => {
      if (s.l_name?.toLowerCase() === parentLastName) return true;
      if (parentAddr && s.street_address?.toLowerCase()?.trim() === parentAddr) return true;
      return false;
    });
  }, [parent, allAvailable]);

  // When "Show all" is on or searching, filter from all available; otherwise show suggestions
  const displayedStudents = useMemo(() => {
    if (!showAll && !search) return suggestedStudents;
    const list = allAvailable;
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    return list.filter((s) =>
      `${s.f_name} ${s.l_name}`.toLowerCase().includes(q) ||
      s.l_name?.toLowerCase().includes(q) ||
      s.f_name?.toLowerCase().includes(q)
    );
  }, [showAll, search, allAvailable, suggestedStudents]);

  const updateLink = async (newChildrenIds, newGuardianStudentPairs) => {
    setSaving(true);
    setError('');
    try {
      // Update parent's children_ids
      await apiFetch(`/contacts/${parentId}`, {
        method: 'PUT',
        body: JSON.stringify({ children_ids: newChildrenIds }),
      }, token);

      // Update each affected student's guardians list
      for (const { studentId, guardians } of newGuardianStudentPairs) {
        await apiFetch(`/students/${studentId}`, {
          method: 'PUT',
          body: JSON.stringify({ guardians_permanent_authorized_driver: guardians }),
        }, token);
      }

      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLink = async (studentId) => {
    const newChildrenIds = [...childrenIds, studentId];

    // Add parent to student's guardians
    const student = schoolStudents.find((s) => s.id === studentId);
    const currentGuardians = student?.guardians_permanent_authorized_driver || student?.['guardians/permanent_authorized_driver'] || [];
    const newGuardians = [...currentGuardians, parent.id];

    await updateLink(newChildrenIds, [{ studentId, guardians: newGuardians }]);
  };

  const handleUnlink = async (studentId) => {
    const newChildrenIds = childrenIds.filter((id) => id !== studentId);

    // Remove parent from student's guardians
    const student = schoolStudents.find((s) => s.id === studentId);
    const currentGuardians = student?.guardians_permanent_authorized_driver || student?.['guardians/permanent_authorized_driver'] || [];
    const newGuardians = currentGuardians.filter((id) => id !== parent.id);

    await updateLink(newChildrenIds, [{ studentId, guardians: newGuardians }]);
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (!parent) return <div className="sa-error">Parent not found</div>;

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        <button onClick={() => navigate('school-detail', schoolId)}>School</button>
        {' / '}
        <button onClick={() => navigate('parents', schoolId)}>Parents</button>
        {' / '}
        {parent.f_name} {parent.l_name}
      </div>

      {error && <div className="sa-error">{error}</div>}

      {/* Parent Info */}
      <div className="sa-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <h2>{parent.f_name} {parent.l_name}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="sa-btn sa-btn-primary" onClick={() => navigate('parent-edit', { parent, schoolId })}>
              Edit Parent
            </button>
            <ResetCodeButton
              token={token}
              userType="contact"
              userId={parent.id}
              userName={`${parent.f_name} ${parent.l_name}`}
              contact={parent}
            />
            <SendOnboardingEmailButton
              token={token}
              parentId={parent.id}
              schoolId={schoolId}
              hasPassword={parent.has_password}
              contact={parent}
            />
            <SendOnboardingWhatsAppButton
              token={token}
              parentId={parent.id}
              schoolId={schoolId}
              hasPassword={parent.has_password}
            />
          </div>
        </div>
        <div className="sa-form-grid" style={{ marginTop: 16 }}>
          <InfoItem label="Email" value={getContactEmailDisplay(parent).label} />
          <InfoItem label="Phone" value={parent.phone} />
          <InfoItem label="Address" value={parent.street_address ? `${parent.street_address}, ${parent.city}, ${parent.state} ${parent.zip_code}` : null} />
          <InfoItem label="Vehicle" value={[parent.car_color, parent.vehicle_make_model].filter(Boolean).join(' ') || null} />
          <InfoItem label="License Plate" value={parent.license_plate} />
          <InfoItem label="Driver License" value={parent.driver_license_number} />
        </div>
      </div>

      {/* Linked Children */}
      <div className="sa-card">
        <h2>Linked Children ({linkedStudents.length})</h2>

        {linkedStudents.length === 0 ? (
          <div className="sa-empty" style={{ padding: 20 }}>No children linked yet. Use the section below to link students.</div>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Gender</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {linkedStudents.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.f_name} {s.l_name}</td>
                  <td>{s.grade}</td>
                  <td>{s.gender}</td>
                  <td>
                    <span className={`sa-badge ${s.status === 'active' ? 'sa-badge-active' : 'sa-badge-inactive'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="sa-btn sa-btn-danger"
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => handleUnlink(s.id)}
                      disabled={saving}
                    >
                      Unlink
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Available Students to Link */}
      <div className="sa-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h2 style={{ marginBottom: 0 }}>
            {!showAll && !search ? 'Suggested Matches' : 'All Students'}{' '}
            ({displayedStudents.length})
          </h2>
          <button
            className="sa-btn sa-btn-secondary"
            style={{ fontSize: 13 }}
            onClick={() => { setShowAll(!showAll); setSearch(''); }}
          >
            {showAll || search ? 'Show suggested only' : 'Search all students'}
          </button>
        </div>

        {!showAll && !search ? (
          <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
            Students matching by last name ({parent.l_name}){parent.street_address ? ` or address (${parent.street_address})` : ''}.
          </p>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search by student name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: '100%', maxWidth: 350 }}
              autoFocus
            />
          </div>
        )}

        {displayedStudents.length === 0 ? (
          <div className="sa-empty" style={{ padding: 20 }}>
            {!showAll && !search
              ? 'No automatic matches found. Try "Search all students" to find and link manually.'
              : search ? 'No students match your search.' : 'All active students are already linked.'}
          </div>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Gender</th>
                <th>Match</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedStudents.map((s) => {
                const nameMatch = s.l_name?.toLowerCase() === parent.l_name?.toLowerCase();
                const addrMatch = parent.street_address && s.street_address?.toLowerCase()?.trim() === parent.street_address?.toLowerCase()?.trim();
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.f_name} {s.l_name}</td>
                    <td>{s.grade}</td>
                    <td>{s.gender}</td>
                    <td>
                      {nameMatch && <span className="sa-badge sa-badge-active" style={{ marginRight: 4 }}>Last name</span>}
                      {addrMatch && <span className="sa-badge sa-badge-trial">Address</span>}
                      {!nameMatch && !addrMatch && '—'}
                    </td>
                    <td>
                      <button
                        className="sa-btn sa-btn-success"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => handleLink(s.id)}
                        disabled={saving}
                      >
                        Link
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}
